import type { UniqueLabel } from "@lxc/app-device-common";
import { filterEmptyValues } from "@lxc/app-device-common";
import type { Ref } from "vue";
import { computed } from "vue";
import filtersUtils from "~/core/utils/filters.utils";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { LogEntityClass } from "~/modules/log/models/LogEntityClass.enum";
import type { TranslatedLogAttribute } from "~/modules/log/models/TranslatedLogAttribute.interface";
import type { Role } from "~/modules/user/models/Role.interface";
import type { User } from "~/modules/user/models/User.interface";
import type { UserGroup } from "~/modules/user/models/UserGroup.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";

interface UseFilterTagsParams {
  actions: Ref<TranslatedLogAttribute[] | undefined>;
  actionTypes: Ref<TranslatedLogAttribute[] | undefined>;
  entityClass: Ref<LogEntityClass | undefined>;
  devices: Ref<Dtwin[]>;
  users: Ref<User[]>;
  actionLabel: string;
  actionTypeLabel: string;
  deviceLabel: string;
  userLabel: string;
  userGroupLabel: string;
  profileLabel: string;
  roleLabel: string;
  sectorLabel: string;
}

export function useLogFilterTags(params: UseFilterTagsParams) {
  function buildAttributeTags(
    attributes: TranslatedLogAttribute[] | undefined,
    label: string,
  ): UniqueLabel[] {
    if (!attributes || attributes.length === 0) return [];

    return filterEmptyValues<UniqueLabel>(
      attributes.map((attr) =>
        filtersUtils.getTag(label, attr.rawValue, attr.translation),
      ),
    );
  }

  function buildEntityTags(
    entityClass?: LogEntityClass,
    devices?: Dtwin[],
    users?: User[],
    userGroups?: UserGroup[],
    roles?: Role[],
    sectors?: UserSessionSector[],
  ): UniqueLabel[] {
    let tagFilters: UniqueLabel[] = [];

    switch (entityClass) {
      case LogEntityClass.DEVICE:
        if (devices) {
          tagFilters = filterEmptyValues<UniqueLabel>(
            devices.map((device) =>
              filtersUtils.getDtwinTag(device, params.deviceLabel),
            ),
          );
        }
        break;
      case LogEntityClass.USER:
        if (users) {
          tagFilters = filterEmptyValues<UniqueLabel>(
            users.map((user) =>
              filtersUtils.getUserTag(user, params.userLabel),
            ),
          );
        }
        break;
      case LogEntityClass.GROUP:
        if (userGroups) {
          tagFilters = filterEmptyValues<UniqueLabel>(
            userGroups.map((group) =>
              filtersUtils.getUserGroupTag(group, params.userGroupLabel),
            ),
          );
        }
        break;
      case LogEntityClass.ROLE:
        if (roles) {
          tagFilters = filterEmptyValues<UniqueLabel>(
            roles.map((role) =>
              filtersUtils.getRoleTag(role, params.roleLabel),
            ),
          );
        }
        break;
      case LogEntityClass.SECTOR:
        if (sectors) {
          tagFilters = filterEmptyValues<UniqueLabel>(
            sectors.map((sector) =>
              filtersUtils.getSectorTag(sector, params.sectorLabel),
            ),
          );
        }
        break;
    }

    return tagFilters;
  }

  function buildAllTags(
    actions?: TranslatedLogAttribute[],
    actionTypes?: TranslatedLogAttribute[],
    entityClass?: LogEntityClass,
    devices?: Dtwin[],
    users?: User[],
    userGroups?: UserGroup[],
    roles?: Role[],
    sectors?: UserSessionSector[],
  ): UniqueLabel[] {
    return [
      ...buildAttributeTags(actions, params.actionLabel),
      ...buildAttributeTags(actionTypes, params.actionTypeLabel),
      ...buildEntityTags(
        entityClass,
        devices,
        users,
        userGroups,
        roles,
        sectors,
      ),
    ];
  }

  const selectedTags = computed((): UniqueLabel[] => {
    return buildAllTags(
      params.actions.value,
      params.actionTypes.value,
      params.entityClass.value,
      params.devices.value,
      params.users.value,
    );
  });

  function deleteTag(
    uid: string,
    actions: Ref<TranslatedLogAttribute[] | undefined>,
    actionTypes: Ref<TranslatedLogAttribute[] | undefined>,
    entityId: Ref<string[] | undefined>,
    entityClass: Ref<LogEntityClass | undefined>,
    devices: Ref<Dtwin[]>,
    users: Ref<User[]>,
  ) {
    // Remove from actions/actionTypes
    actions.value = actions.value?.filter((a) => a.rawValue !== uid);
    actionTypes.value = actionTypes.value?.filter((at) => at.rawValue !== uid);

    // Remove from entity list
    const idIndex = entityId.value?.findIndex((id) => id === uid);
    if (idIndex !== undefined && idIndex >= 0) {
      entityId.value?.splice(idIndex, 1);
    }

    // Remove from devices/users
    const deviceIndex = devices.value.findIndex((d) => d.uid === uid);
    if (deviceIndex >= 0) {
      devices.value.splice(deviceIndex, 1);
    }

    const userIndex = users.value.findIndex((u) => u.login === uid);
    if (userIndex >= 0) {
      users.value.splice(userIndex, 1);
    }

    // Clear entity class if no entities left
    if (!entityId.value?.length && entityClass.value !== undefined) {
      entityClass.value = undefined;
    }
  }

  return {
    selectedTags,
    buildAllTags,
    buildEntityTags,
    buildAttributeTags,
    deleteTag,
  };
}
